<script setup>
import { computed, inject } from 'vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const { variant, orientation, inReplyTo } = useMessageContext();
const { t } = useI18n();

// Group header data is provided by Message.vue via provide().
// Bypasses the slot chain (Vue 3 doesn't propagate named slots through
// intermediate bubble components).
const injectedGroupHeaderData = inject('groupHeaderData', null);
const groupHeaderData = computed(() => injectedGroupHeaderData?.value ?? null);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-s2-msg-agent text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]:
    'bg-n-s2-msg-user text-n-slate-12 shadow-sm dark:shadow-none',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-9 !text-[10px]',
  [MESSAGE_VARIANTS.BOT]:
    'bg-n-s2-msg-bot-bg text-n-s2-msg-bot-text border border-n-s2-msg-bot-border',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-wpp-bg text-n-wpp-text',
  [MESSAGE_VARIANTS.AUTOMATED]:
    'bg-n-s2-msg-auto-bg text-n-s2-msg-auto-text border border-n-s2-msg-auto-border',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]: 'bg-n-solid-amber text-n-amber-12',
  [MESSAGE_VARIANTS.EXTERNAL_ECHO]: 'bg-n-s2-msg-agent text-n-slate-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-[14px] ltr:rounded-bl-[4px] rtl:rounded-br-[4px]',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-[14px] ltr:rounded-br-[4px] rtl:rounded-bl-[4px]',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-[13px] leading-[1.5]"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="groupHeaderData"
      class="flex items-center gap-1.5 px-[13px] pt-[9px] mb-1.5"
    >
      <img
        v-if="groupHeaderData.senderJid"
        :src="groupHeaderData.avatarUrl"
        class="size-5 rounded-full object-cover"
        @error="$event.target.style.display = 'none'"
      />
      <span class="text-[11px] font-semibold text-n-teal-11">
        {{ groupHeaderData.displayName }}
      </span>
    </div>
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <slot />
  </div>
</template>
